/**
 * @file form - relationFormItems
 * @author sunwen05
 */

import _ from 'lodash';
import React, {useCallback, useState, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Form, Popover, Button, Checkbox} from 'acud';
import {OutlinedDown, OutlinedUp, OutlinedPlus} from 'acud-icon';

import {
    DuFormItemControl,
    OnDisplayItem,
    DISPLAY_ITEM_RELATION,
    RelationFormItemsProps
} from '../../../../hooks/biz/form/interface';

import DynamicFormItem from './DynamicFormItem';
import DuFormItem from './DuFormItem';
import {expressionComparison} from '../formUtil';

const CheckboxGroup = Checkbox.Group;

interface RelationProps extends RelationFormItemsProps {
    isHasQueryPanel?: boolean;
    cols?: number;
    onReset?: any;
    showQueryNames?: string[];
    setShowQueryNames?: (list: string[]) => void;
    defaultCheckedList?: string[];
}

const RelationFormItems: React.FunctionComponent<RelationProps> = props => {
    const {
        prefixCls, controls, option, formData, form = {}, moreSettingCollapsed,
        isHasQueryPanel, defaultCheckedList = [], onReset, showQueryNames = [], setShowQueryNames
    } = props || {};

    const colLabel = moreSettingCollapsed ? moreSettingCollapsed.label : undefined;
    const defaultExpand = moreSettingCollapsed ? moreSettingCollapsed.defaultExpand : undefined;
    const expand = moreSettingCollapsed?.expand;
    const setExpand = moreSettingCollapsed?.setExpand;
    const expandIdx = colLabel ? controls.findIndex(item => item.name === colLabel) : 0;

    const [colExpand, setColExpand] = useState(defaultExpand);

    const handleColExpandChange = useCallback(() => {
        setExpand?.(!colExpand);
        setColExpand(!colExpand);
    }, [colExpand, setExpand]);

    // 初始化时调用 TODO !! 考虑nameList
    // 如果配置了高级配置，必须重写validateFileds
    useEffect(() => {
        if (moreSettingCollapsed && form) {
            // eslint-disable-next-line no-console
            console.log('moreSettingCollapsed(高级配置) 改写 validateFields!!');
            const e = form.validateFields;
            const validateFields : any = async (nameList: any, options: any) => {
                try {
                    // 只有校验成功了，才会执行这一步，否则Promise Reject了
                    return await e(nameList, options);
                }
                catch (e: any) {
                    // 如果高级配置里面有问题需要打开
                    e?.errorFields?.every((field: any) => {
                        let {name = []} = field;
                        name = name[0];
                        const index = controls.findIndex(item => item.name === name);
                        if (index > expandIdx) {
                            setColExpand(true);
                            return false;
                        }
                        return true;
                    });
                    return Promise.reject(e);
                }
            };
            form.validateFields = validateFields;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const relationComparison = useCallback((oper: DISPLAY_ITEM_RELATION, rawData: boolean, newData: boolean) => {
        switch (oper) {
            case '&&':
                return rawData && newData;
            default:
                return rawData || newData;
        }
    }, []);

    const getOnDisplay = useCallback((getFieldValue: any, onDisplay: OnDisplayItem[]) => {
        let result = true;
        _.each(onDisplay, (item, i) => {
            const formItemValue = getFieldValue(item.name);
            const value = formItemValue === undefined ? formData[item.name] : formItemValue;
            let itemResult = false;
            _.each(item.content, (values: any, index: number) => {
                const res = expressionComparison(values.exp, values.value, value, values.customMethod);
                itemResult = index === 0 ? res : relationComparison(values.relation, itemResult, res);
            });
            result = i === 0 ? itemResult : relationComparison(item.relation, result, itemResult);
        });
        return result;
    },
    [formData, relationComparison]
    );

    useEffect(() => {
        if (expand) {
            setColExpand?.(expand);
        }
    }, [expand]);

    const [, setVisible] = useState(false);
    const [checkedList, setCheckedList] = useState(defaultCheckedList);
    const [indeterminate, setIndeterminate] = React.useState(
        showQueryNames.length > 0 && showQueryNames.length < controls.length
    );
    const [checkAll, setCheckAll] = React.useState(showQueryNames.length === controls.length);

    const showQueryInfo = useMemo(() => {
        const showBtn = showQueryNames.length > 1;
        const showQueryItems = controls.length >= 2;
        return {
            showBtn,
            showQueryItems,
            queryItemsOptions: controls.map(item => item?.label || '')
        };
    }, [controls, showQueryNames]);

    const onChange = useCallback(list => {
        setCheckedList(list);
        setIndeterminate(!!list.length && list.length < showQueryInfo.queryItemsOptions.length);
        setCheckAll(list.length === controls.length);
    }, [showQueryInfo, controls]);

    const onCheckAllChange = useCallback(e => {
        const options = showQueryInfo.queryItemsOptions;
        setCheckedList(e.target.checked ? options : [...options].slice(0, 2));
        setIndeterminate(e.target.checked ? false : options.length !== 2);
        setCheckAll(e.target.checked ? true : options.length === 2);
    }, [showQueryInfo]);

    const handleVisibleChange = useCallback(flag => {
        setVisible(flag);
        if (!flag && setShowQueryNames) {
            setShowQueryNames(checkedList);
        }
    }, [checkedList, setShowQueryNames]);

    // const complementItemKeyList = useMemo(() => {
    //     const len = showQueryNames.length;
    //     const num = (cols || 1) - len % (cols || 1) - 1;
    //     const key = new Date().getTime();
    //     return new Array(num).fill('').map((_, i) => `${key}-${i}`);
    // }, [showQueryNames, cols]);

    return (
        <>
            {_.map(controls, (control: DuFormItemControl, index: number) => {
                const isHidden = (moreSettingCollapsed && colLabel && !colExpand && index > expandIdx);
                const itemStyle : React.CSSProperties | undefined
                    = isHidden
                        ? {visibility: 'hidden', height: 0, opacity: 0}
                        : {};
                return (
                    control && (
                        <>
                            {
                                isHasQueryPanel ? (
                                    <>
                                        {
                                            showQueryNames.includes(control?.label || '') ? (
                                                <Form.Item
                                                    shouldUpdate
                                                    style={itemStyle}
                                                    key={control.name}
                                                    noStyle={option.noStyle && !isHidden}
                                                >
                                                    <DuFormItem
                                                        prefixCls={prefixCls}
                                                        key={control.name}
                                                        isHasQueryPanel
                                                        control={control}
                                                        option={option}
                                                        formData={formData}
                                                    />
                                                </Form.Item>
                                            ) : null
                                        }
                                    </>
                                ) : (
                                    <Form.Item
                                        shouldUpdate
                                        style={itemStyle}
                                        key={control.name}
                                        noStyle={option.noStyle && !isHidden}
                                    >
                                        {({getFieldValue}) => {
                                            const isShowItem = !control?.onDisplay
                                                || getOnDisplay(getFieldValue, control?.onDisplay);
                                            return isShowItem ? (
                                                control?.dynamic ? (
                                                    <DynamicFormItem
                                                        prefixCls={prefixCls}
                                                        key={control.name}
                                                        control={control}
                                                        option={option}
                                                        formData={formData}
                                                        form={form}
                                                    />
                                                ) : (
                                                    <DuFormItem
                                                        prefixCls={prefixCls}
                                                        key={control.name}
                                                        control={control}
                                                        option={option}
                                                        formData={formData}
                                                    />
                                                )
                                            ) : null;
                                        }}
                                    </Form.Item>
                                )
                            }
                            {colLabel && control?.name === colLabel && (
                                <div className="more-setting-collapsed" onClick={handleColExpandChange}>
                                    <span className="btn-txt">
                                        {colExpand ? <OutlinedUp width={16} /> : <OutlinedDown width={16} />}
                                        {moreSettingCollapsed.expandTitle ?? '高级配置'}
                                    </span>
                                </div>
                            )}
                        </>
                    )
                );
            })}
            {
                isHasQueryPanel ? (
                    <>
                        {/* {
                            _.map(complementItemKeyList, key => <Form.Item key={key}><span></span></Form.Item>)
                        } */}
                        {
                            showQueryInfo.showQueryItems ? (
                                <Form.Item
                                    className={`${prefixCls}-footer ${showQueryNames.length === 1 ? 'no-query' : ''}`}
                                >
                                    <>
                                        <Popover
                                            placement='bottomLeft'
                                            overlayClassName={`${prefixCls}-footer-popover-root`}
                                            content={
                                                <div className={`${prefixCls}-footer-popover`}>
                                                    <Checkbox
                                                        indeterminate={indeterminate}
                                                        onChange={onCheckAllChange}
                                                        checked={checkAll}
                                                        style={{marginRight: 16}}
                                                    >
                                                        全选
                                                    </Checkbox>
                                                    <CheckboxGroup
                                                        disabled={checkedList.length === 2}
                                                        options={showQueryInfo.queryItemsOptions}
                                                        value={checkedList}
                                                        onChange={onChange}
                                                    />
                                                </div>
                                            }
                                            onVisibleChange={handleVisibleChange}
                                        >
                                            <OutlinedPlus />
                                            <span>筛选项</span>
                                        </Popover>
                                        {
                                            showQueryInfo.showBtn ? (
                                                <>
                                                    <Button
                                                        style={{marginLeft: 16, marginRight: 8}}
                                                        type="primary"
                                                        htmlType="submit"
                                                    >
                                                        查询
                                                    </Button>
                                                    <Button htmlType="button" onClick={onReset}>
                                                        重置
                                                    </Button>
                                                </>
                                            ) : null
                                        }
                                    </>
                                </Form.Item>
                            ) : null
                        }
                    </>) : null
            }
        </>
    );
};

RelationFormItems.propTypes = {
    controls: PropTypes.array.isRequired,
    option: PropTypes.object.isRequired,
    form: PropTypes.object.isRequired,
    formData: PropTypes.object
};

RelationFormItems.defaultProps = {
    controls: [],
    option: {},
    formData: {},
    form: {}
};

export default RelationFormItems;
